import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import MainLayout  from './components/layout/MainLayout';
import PageLoader  from './components/common/PageLoader';
import { usePageTracking } from './hooks/usePageTracking';

/**
 * Route-based code splitting with React.lazy + Suspense.
 *
 * Each page is split into its own chunk so visitors only download
 * the code for the page they are actually viewing. This directly
 * improves Lighthouse Performance (TTI, TBT, FCP) and Core Web Vitals.
 *
 * Home is loaded eagerly — it is the entry point and should never
 * be deferred. All other pages are lazy-loaded on first navigation.
 */
import Home from './pages/Home';

const About          = lazy(() => import('./pages/About'));
const Services       = lazy(() => import('./pages/Services'));
const Products       = lazy(() => import('./pages/Products'));
const Industries     = lazy(() => import('./pages/Industries'));
const Careers        = lazy(() => import('./pages/Careers'));
const Contact        = lazy(() => import('./pages/Contact'));
const Privacy        = lazy(() => import('./pages/Privacy'));
const Terms          = lazy(() => import('./pages/Terms'));
const NotFound       = lazy(() => import('./pages/NotFound'));

// ── Authority Expansion Pages ────────────────────────────────────
const OurProcess       = lazy(() => import('./pages/OurProcess'));
const Technology       = lazy(() => import('./pages/Technology'));
const HealthcareIT     = lazy(() => import('./pages/industries/HealthcareIT'));
const EdTechPlatforms  = lazy(() => import('./pages/industries/EdTechPlatforms'));
const ERPAutomation    = lazy(() => import('./pages/industries/ERPAutomation'));
const SaaSProduct      = lazy(() => import('./pages/industries/SaaSProduct'));
const EnterpriseCustom = lazy(() => import('./pages/industries/EnterpriseCustom'));

// ── Solution Pillars ─────────────────────────────────────────────
const EnterpriseSoftware = lazy(() => import('./pages/solutions/EnterpriseSoftware'));
const CustomSoftware     = lazy(() => import('./pages/solutions/CustomSoftware'));
const SaaSDevelopment    = lazy(() => import('./pages/solutions/SaaSDevelopment'));
const AiMlSolutions      = lazy(() => import('./pages/solutions/AiMlSolutions'));

// ── Blog ─────────────────────────────────────────────────────────
const Blog        = lazy(() => import('./pages/blog/Blog'));
const BlogArticle = lazy(() => import('./pages/blog/BlogArticle'));

// ── Admin (lazy-loaded, isolated from public site) ──────────────
const AdminLogin     = lazy(() => import('./pages/admin/AdminLogin'));
const AdminLayout    = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminProducts     = lazy(() => import('./pages/admin/AdminProducts'));
const AdminTestimonials = lazy(() => import('./pages/admin/AdminTestimonials'));
const LeadsList      = lazy(() => import('./pages/admin/LeadsList'));
const LeadDetails    = lazy(() => import('./pages/admin/LeadDetails'));
const ExportPage     = lazy(() => import('./pages/admin/ExportPage'));const VisitorPage    = lazy(() => import('./pages/admin/VisitorPage'));
/**
 * App — root routing configuration.
 *
 * Route map:
 *   /                → Home
 *   /about           → About
 *   /services        → Services
 *   /products        → Products
 *   /industries      → Industries
 *   /careers         → Careers
 *   /contact         → Contact
 *   /privacy-policy  → Privacy
 *   /terms           → Terms
 *   *                → NotFound (404)
 *
 * Admin routes (no public layout wrapper):
 *   /admin/login     → AdminLogin  (unauthenticated)
 *   /admin           → AdminLayout > AdminDashboard
 *   /admin/leads     → AdminLayout > LeadsList
 *   /admin/leads/:id → AdminLayout > LeadDetails
 *   /admin/export    → AdminLayout > ExportPage
 */
/**
 * AppRoutes — inner component that can call hooks (needs Router context).
 */
function AppRoutes() {
  // Silently fires a page-view beacon on every route change
  usePageTracking();

  return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* ── Public site ─────────────────────────────────── */}
          <Route element={<MainLayout />}>
            <Route path="/"               element={<Home />}       />
            <Route path="/about"          element={<About />}      />
            <Route path="/services"       element={<Services />}   />
            <Route path="/products"       element={<Products />}   />
            <Route path="/industries"     element={<Industries />} />
            <Route path="/careers"        element={<Careers />}    />
            <Route path="/contact"        element={<Contact />}    />
            <Route path="/privacy-policy" element={<Privacy />}    />
            <Route path="/terms"          element={<Terms />}      />

            {/* ── Authority Expansion ─────────────────────────── */}
            <Route path="/our-process"                element={<OurProcess />}       />
            <Route path="/technology"                 element={<Technology />}       />
            <Route path="/case-studies/:slug"         element={<Navigate to="/products" replace />} />
            <Route path="/industries/healthcare"      element={<HealthcareIT />}     />
            <Route path="/industries/edtech"          element={<EdTechPlatforms />}  />
            <Route path="/industries/erp-automation"  element={<ERPAutomation />}    />
            <Route path="/industries/saas"            element={<SaaSProduct />}      />
            <Route path="/industries/enterprise"      element={<EnterpriseCustom />} />

            {/* ── Solution Pillars ─────────────────────────── */}
            <Route path="/solutions/enterprise-software" element={<EnterpriseSoftware />} />
            <Route path="/solutions/custom-software"     element={<CustomSoftware />}     />
            <Route path="/solutions/saas-development"    element={<SaaSDevelopment />}    />
            <Route path="/solutions/ai-ml-solutions"     element={<AiMlSolutions />}      />

            {/* ── Blog ─────────────────────────────────────── */}
            <Route path="/blog"       element={<Blog />}        />
            <Route path="/blog/:slug" element={<BlogArticle />} />

            <Route path="*"               element={<NotFound />}   />
          </Route>

          {/* ── Admin — unauthenticated ──────────────────────── */}
          <Route path="/admin/login" element={<AdminLogin />} />

          {/* ── Admin — authenticated (guard inside AdminLayout) */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index                  element={<AdminDashboard />} />
            <Route path="leads"           element={<LeadsList />}      />
            <Route path="leads/:id"       element={<LeadDetails />}    />
            <Route path="export"          element={<ExportPage />}     />
            <Route path="visitors"        element={<VisitorPage />}    />
            <Route path="products"        element={<AdminProducts />}  />
            <Route path="testimonials"    element={<AdminTestimonials />} />
          </Route>
        </Routes>
      </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

export default App;
